import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  exchangeCodeForToken,
  initiateOAuthFlow,
  isValidAuthorizationCode,
  isValidIssuer,
  isValidLaunch,
  sendPatientTokenData,
} from "./fhirUtils";
import { useAppDispatch } from "../../store/hooks";
import { patientAuthSliceActions } from "./PatientAuthSlice";
import { getJwtExpiry } from "../../utility/functions/common-functions";

interface FhirAuthContextType {
  tokenResponse: Record<string, any> | null;
  error: string | null;
  /** clear both React state + Redux slice */
  reset: () => void;
}

export const FhirAuthContext = createContext<FhirAuthContextType>({
  tokenResponse: null,
  error: null,
  reset: () => {}, // provide a noop function to avoid undefined errors
});

// Module‐level placeholder for the “true” reset implementation
let _resetFhirAuth: () => void = () => {};

export function resetFhirAuthContext() {
  _resetFhirAuth();
}

export const FhirAuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const SEND_TOKEN_API = process.env.REACT_APP_SEND_TOKEN_API === "true";

  const [tokenResponse, setTokenResponse] = useState<Record<
    string,
    any
  > | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTokenResponse(null);
    setError(null);
  };

  // Wire up our module‐level placeholder on mount
  useEffect(() => {
    _resetFhirAuth = reset;
    return () => {
      _resetFhirAuth = () => {};
    };
  }, [dispatch]);

  // OAuth flow
  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);
    const iss = queryParameters.get("iss")?.trim() || null;
    const launch = queryParameters.get("launch")?.trim() || null;
    const code = queryParameters.get("code")?.trim() || null;

    const validateParams = () => {
      if (code && !isValidAuthorizationCode(code)) {
        throw new Error("Invalid authorization code format.");
      }
      if (iss && !isValidIssuer(iss)) {
        throw new Error("Invalid 'iss' parameter.");
      }
      if (launch && !isValidLaunch(launch)) {
        throw new Error("Invalid 'launch' parameter.");
      }
      if (!code && (!iss || !launch)) {
        console.warn("Missing 'iss', 'launch', or 'code' parameters.");
        throw new Error(
          "At least one of 'iss', 'launch', or 'code' parameters must be present."
        );
      }
    };

    const runOAuthFlow = async () => {
      try {
        validateParams();
        if (code) {
          const tokenData = await exchangeCodeForToken(code);
          setTokenResponse(tokenData);

          const expiry = getJwtExpiry(tokenData.access_token) || 0;
          dispatch(
            patientAuthSliceActions.setPatientAuthToken(tokenData.access_token)
          );
          dispatch(patientAuthSliceActions.setPatientFhirId(tokenData.patient));
          dispatch(patientAuthSliceActions.setTokenExpiry(expiry));

          if (SEND_TOKEN_API) {
            await sendPatientTokenData({
              PatientFhirId: tokenData.patient,
              AccessToken: tokenData.access_token,
              ExpiresInSecond: tokenData.expires_in,
            });
          }
        } else if (iss && launch) {
          await initiateOAuthFlow(iss, launch);
        } else {
          console.warn("Missing 'iss', 'launch', or 'code' parameters.");
        }
      } catch (e) {
        console.error(e);
        setError("An error occurred during the OAuth process.");
      }
    };

    runOAuthFlow();
  }, [dispatch, SEND_TOKEN_API]);

  return (
    <FhirAuthContext.Provider value={{ tokenResponse, error, reset }}>
      {children}
    </FhirAuthContext.Provider>
  );
};

export const useFhirAuth = () => {
  const context = useContext(FhirAuthContext);
  if (!context) {
    throw new Error("useFhirAuth must be used within a FhirAuthProvider");
  }
  return context;
};
